import { germanPublicHolidays, type HolidayDefinition } from '@/utils/holidays/de'
import { DateTime, type HolidayMatcher } from 'luxon-business-days'

type BusinessDateTime = DateTime<true>

const configureGermanBusinessCalendar = () => {
  const dt = DateTime.local()
  const { getEasterMonthAndDay } = dt.availableHolidayHelpers
  const isSameDay = (left: BusinessDateTime, right: BusinessDateTime) => left.toISODate() === right.toISODate()
  const isFixedHoliday = (month: number, day: number) => (inst: BusinessDateTime) =>
    inst.month === month && inst.day === day
  const easterSunday = (year: number, zone: string) => {
    const [month, day] = getEasterMonthAndDay(year)
    return DateTime.fromObject({ year, month, day }, { zone })
  }
  const isRelativeToEaster = (offsetDays: number) => (inst: BusinessDateTime) => {
    const base = easterSunday(inst.year, inst.zoneName)
    return isSameDay(inst, base.plus({ days: offsetDays }) as BusinessDateTime)
  }

  const holidayMatchers: HolidayMatcher[] = germanPublicHolidays.map((holiday) => {
    const matches =
      holiday.type === 'fixed' ? isFixedHoliday(holiday.month, holiday.day) : isRelativeToEaster(holiday.offsetDays)

    return (inst) => matches(inst as BusinessDateTime)
  })

  dt.setupBusiness({ holidayMatchers })
}

configureGermanBusinessCalendar()

const toHolidayDate = (holiday: HolidayDefinition, year: number): Date => {
  if (holiday.type === 'fixed') {
    return DateTime.fromObject({
      year,
      month: holiday.month,
      day: holiday.day,
    }).toJSDate()
  }

  const [month, day] = DateTime.local().availableHolidayHelpers.getEasterMonthAndDay(year)
  return DateTime.fromObject({ year, month, day }).plus({ days: holiday.offsetDays }).toJSDate()
}

const getGermanHolidayDates = (startYear: number, endYear: number): Date[] => {
  const years = []
  for (let year = startYear; year <= endYear; year += 1) {
    years.push(year)
  }

  return years.flatMap((year) => germanPublicHolidays.map((holiday) => toHolidayDate(holiday, year)))
}

export { DateTime, getGermanHolidayDates }
